import { Fragment, Node as PMNode, NodeType, Schema } from "@tiptap/pm/model";

// Collapse any content into a single paragraph of text + hardBreaks.
// Marks are dropped, block types (headings, lists, tables, etc.) are
// dissolved, and block boundaries become hardBreaks. Empty blocks between
// content preserve the blank line (two consecutive hardBreaks). Non-text,
// non-hardBreak inline nodes (images, embeds) are dropped.
export const flattenToPlainParagraph = (
  fragment: Fragment,
  schema: Schema
): PMNode => {
  const paragraph = schema.nodes.paragraph;
  const hardBreak = schema.nodes.hardBreak;
  if (!paragraph || !hardBreak) {
    throw new Error(
      "flattenToPlainParagraph: schema must define paragraph and hardBreak"
    );
  }

  const inline: PMNode[] = [];
  let pendingBreak = false;

  const flushPending = () => {
    if (pendingBreak) {
      inline.push(hardBreak.create());
      pendingBreak = false;
    }
  };

  const visit = (node: PMNode) => {
    if (node.isText) {
      flushPending();
      inline.push(schema.text(node.text!));
      return;
    }
    if (node.type === hardBreak) {
      flushPending();
      inline.push(hardBreak.create());
      return;
    }
    if (node.isBlock) {
      flushPending();
      node.forEach(visit);
      if (inline.length > 0) pendingBreak = true;
    }
    // other inline leaf nodes (images, embeds) are dropped
  };

  fragment.forEach(visit);

  return paragraph.create(null, Fragment.fromArray(inline));
};

// Collapse consecutive paragraph siblings into a single paragraph separated
// by hardBreaks, matching the component's Enter-is-soft-break keyboard
// behavior. Lists, headings, tables, and other block types are preserved.
export const flattenParagraphSiblings = (
  fragment: Fragment,
  schema: Schema
): Fragment => {
  const paragraph = schema.nodes.paragraph;
  const hardBreak = schema.nodes.hardBreak;
  if (!paragraph || !hardBreak) return fragment;

  const result: PMNode[] = [];
  let pending: PMNode[] | null = null;

  const flushPending = () => {
    if (pending) {
      result.push(paragraph.create(null, Fragment.fromArray(pending)));
      pending = null;
    }
  };

  fragment.forEach((child) => {
    if (child.type === paragraph) {
      const inline: PMNode[] = [];
      child.content.forEach((c) => inline.push(c));
      if (pending === null) {
        pending = inline;
      } else {
        pending.push(hardBreak.create());
        pending.push(...inline);
      }
    } else {
      flushPending();
      result.push(child.copy(flattenParagraphSiblings(child.content, schema)));
    }
  });
  flushPending();

  return Fragment.fromArray(result);
};

// Rebuild a table's first row using tableHeader cells.
const headerizeFirstRow = (table: PMNode, tableHeader: NodeType): PMNode => {
  const firstRow = table.firstChild;
  if (!firstRow) return table;

  const headerCells: PMNode[] = [];
  firstRow.forEach((cell) => {
    headerCells.push(tableHeader.create(cell.attrs, cell.content, cell.marks));
  });
  const newFirstRow = firstRow.type.create(
    firstRow.attrs,
    Fragment.fromArray(headerCells)
  );

  const rows: PMNode[] = [newFirstRow];
  table.forEach((row, _offset, index) => {
    if (index > 0) rows.push(row);
  });

  return table.type.create(table.attrs, Fragment.fromArray(rows));
};

// Lay a table's cells onto a grid honoring colspan/rowspan, then rebuild it as
// a plain rectangular grid of 1x1 cells: the original content stays in the
// origin (top-left) cell of each merged region and the covered cells become
// empty. GFM markdown tables cannot express spans, so this is what lets a Word
// table with merged cells survive serialization (instead of being dropped to a
// "[table]" placeholder). Tables with no spans are returned unchanged.
const splitTable = (table: PMNode, schema: Schema): PMNode => {
  const tableCell = schema.nodes.tableCell;
  const paragraph = schema.nodes.paragraph;
  const emptyCell = () => tableCell.create(null, paragraph.create());

  const single = (cell: PMNode): PMNode =>
    cell.type.create(
      { ...cell.attrs, colspan: 1, rowspan: 1, colwidth: null },
      cell.content,
      cell.marks
    );

  const grid: Array<Map<number, PMNode>> = [];
  const ensureRow = (r: number) => {
    while (grid.length <= r) grid.push(new Map());
  };

  const inputRows: PMNode[] = [];
  table.forEach((row) => inputRows.push(row));

  let hadSpan = false;

  inputRows.forEach((row, r) => {
    ensureRow(r);
    let col = 0;
    row.forEach((cell) => {
      while (grid[r].has(col)) col++;
      const colspan = cell.attrs.colspan || 1;
      const rowspan = cell.attrs.rowspan || 1;
      if (colspan > 1 || rowspan > 1) hadSpan = true;

      for (let dr = 0; dr < rowspan; dr++) {
        ensureRow(r + dr);
        for (let dc = 0; dc < colspan; dc++) {
          grid[r + dr].set(
            col + dc,
            dr === 0 && dc === 0 ? single(cell) : emptyCell()
          );
        }
      }
      col += colspan;
    });
  });

  if (!hadSpan) return table;

  let width = 0;
  grid.forEach((rowMap) =>
    rowMap.forEach((_cell, c) => {
      if (c + 1 > width) width = c + 1;
    })
  );

  const outRows = grid.map((rowMap, r) => {
    const cells: PMNode[] = [];
    for (let c = 0; c < width; c++) cells.push(rowMap.get(c) ?? emptyCell());
    const rowType = inputRows[r]?.type ?? inputRows[0].type;
    const rowAttrs = inputRows[r]?.attrs ?? null;
    return rowType.create(rowAttrs, Fragment.fromArray(cells));
  });

  return table.type.create(table.attrs, Fragment.fromArray(outRows));
};

// Walk a pasted fragment and un-merge any tables with spanned cells (see
// splitTable). Tables without spans, and non-table content, pass through.
export const splitMergedTableCells = (
  fragment: Fragment,
  schema: Schema
): Fragment => {
  const tableCell = schema.nodes.tableCell;
  const paragraph = schema.nodes.paragraph;
  if (!tableCell || !paragraph) return fragment;

  const result: PMNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === "table") {
      result.push(splitTable(node, schema));
    } else if (node.childCount > 0) {
      // Recurse in case a table is nested inside another block.
      result.push(node.copy(splitMergedTableCells(node.content, schema)));
    } else {
      result.push(node);
    }
  });

  return Fragment.fromArray(result);
};

// Pasted tables (notably from Word) often have no header row — every cell is a
// tableCell. GFM markdown tables require a header row, so tiptap-markdown can't
// serialize a header-less table and drops it, leaving a literal "[table]"
// placeholder. Promote the first row of any header-less table to tableHeader
// cells so the table survives the markdown round-trip. Tables that already have
// a header cell in their first row are left untouched.
export const promoteHeaderlessTables = (
  fragment: Fragment,
  schema: Schema
): Fragment => {
  const tableHeader = schema.nodes.tableHeader;
  if (!tableHeader) return fragment;

  const result: PMNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === "table") {
      const firstRow = node.firstChild;
      let hasHeader = false;
      firstRow?.forEach((cell) => {
        if (cell.type === tableHeader) hasHeader = true;
      });
      result.push(hasHeader ? node : headerizeFirstRow(node, tableHeader));
    } else if (node.childCount > 0) {
      // Recurse in case a table is nested inside another block.
      result.push(node.copy(promoteHeaderlessTables(node.content, schema)));
    } else {
      result.push(node);
    }
  });

  return Fragment.fromArray(result);
};
